'use client';

import { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useGetDemoBookingConfig, useSaveDemoBookingConfig } from '@/hooks/useDemoBookingQuery';
import {
  Loader2, CalendarDays, Clock, AlertTriangle, MessageSquareText,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  SectionCard, RadioCard, FieldLabel, InfoBanner, StickyActionBar,
} from './shared';

interface IDemoBookingProps {
  token: string;
}

interface ICalendar {
  id: number;
  platform: string;
  email: string;
  display_name: string;
  enabled: boolean;
}

interface IDayAvailability {
  enabled: boolean;
  start: string; // "HH:mm"
  end: string; // "HH:mm"
}

interface IAvailability {
  timezone: string;
  slot_minutes: number;
  buffer_minutes?: number;
  days: Record<string, IDayAvailability>;
}

const WEEKDAYS: { key: string; label: string }[] = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

const SLOT_DURATION_OPTIONS = [15, 30, 45, 60];

// Common business timezones; the visitor's own zone is added if missing.
const TIMEZONE_OPTIONS = [
  'America/Los_Angeles',
  'America/Denver',
  'America/Chicago',
  'America/New_York',
  'America/Sao_Paulo',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Singapore',
  'Asia/Tokyo',
  'Australia/Sydney',
  'UTC',
];

const getBrowserTimezone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Los_Angeles';
  } catch {
    return 'America/Los_Angeles';
  }
};

const buildDefaultAvailability = (): IAvailability => ({
  timezone: getBrowserTimezone(),
  slot_minutes: 30,
  buffer_minutes: 0,
  days: WEEKDAYS.reduce((acc, d) => {
    acc[d.key] = {
      enabled: d.key !== 'saturday' && d.key !== 'sunday',
      start: '09:00',
      end: '17:00',
    };
    return acc;
  }, {} as Record<string, IDayAvailability>),
});

// Continental regions for calendar mapping
const CONTINENTS = [
  { id: 'north_america', name: 'North America', description: 'United States, Canada, Mexico' },
  { id: 'south_america', name: 'South America', description: 'Brazil, Argentina, Chile, etc.' },
  { id: 'europe', name: 'Europe', description: 'UK, Germany, France, etc.' },
  { id: 'asia', name: 'Asia', description: 'China, India, Japan, etc.' },
  { id: 'africa', name: 'Africa', description: 'South Africa, Nigeria, etc.' },
  { id: 'oceania', name: 'Oceania', description: 'Australia, New Zealand' },
  { id: 'default', name: 'Default (Fallback)', description: 'For unmapped or unknown regions' },
];

const DemoBooking: React.FC<IDemoBookingProps> = ({ token }) => {
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [regionalMode, setRegionalMode] = useState(false);
  const [regionalMappings, setRegionalMappings] = useState<Record<string, { calendar: string }>>({});
  const [savedRegionalMappings, setSavedRegionalMappings] = useState<Record<string, { calendar: string }>>({});
  const [hasStaleCalendars, setHasStaleCalendars] = useState(false);
  const [availability, setAvailability] = useState<IAvailability>(buildDefaultAvailability());
  const [demoConfig, setDemoConfig] = useState({
    title: 'Book a demo',
    description: 'Schedule a personalized demo with our team',
    action_text: 'Book Demo',
  });

  // Data fetching hooks
  const { data: configData, isLoading, error } = useGetDemoBookingConfig(token);
  const { mutate: saveConfig, isPending: saving } = useSaveDemoBookingConfig();

  // Initialize state when data loads
  useEffect(() => {
    if (configData?.demoConfig) {
      const config = configData.demoConfig;
      // Explicitly handle null/undefined values for consistent boolean state
      setDemoEnabled(Boolean(config.active_status));
      setDemoConfig({
        title: config.title || 'Book a demo',
        description: config.description || 'Schedule a personalized demo with our team',
        action_text: config.action_text || 'Book Demo',
      });

      // Handle regional calendar configuration
      const calendarConfig: any = config.calendar_config || {};
      const isRegional = Boolean(calendarConfig.regional_mode);
      setRegionalMode(isRegional);

      // Load booking availability (business hours), merging onto defaults so any
      // missing weekday is still represented in the editor.
      if (calendarConfig.availability && typeof calendarConfig.availability === 'object') {
        const saved = calendarConfig.availability;
        const defaults = buildDefaultAvailability();
        setAvailability({
          timezone: saved.timezone || defaults.timezone,
          slot_minutes: Number(saved.slot_minutes) || defaults.slot_minutes,
          buffer_minutes: Number(saved.buffer_minutes) || 0,
          days: WEEKDAYS.reduce((acc, d) => {
            const sd = saved.days?.[d.key];
            acc[d.key] = sd
              ? {
                enabled: Boolean(sd.enabled),
                start: sd.start || defaults.days[d.key].start,
                end: sd.end || defaults.days[d.key].end,
              }
              : defaults.days[d.key];
            return acc;
          }, {} as Record<string, IDayAvailability>),
        });
      }

      // Always preserve regional mappings if they exist, regardless of current mode
      if (calendarConfig.mappings) {
        const mappings: Record<string, { calendar: string }> = {};
        const validMappings: Record<string, { calendar: string }> = {};

        Object.entries(calendarConfig.mappings).forEach(([continent, mapping]: [string, any]) => {
          if (mapping.auth_id) {
            const calendarId = mapping.auth_id.toString();
            mappings[continent] = { calendar: calendarId };

            // Only include in valid mappings if calendar still exists
            const calendarExists = configData?.availableCalendars?.some(
              (cal: ICalendar) => cal.id.toString() === calendarId,
            );
            if (calendarExists) {
              validMappings[continent] = { calendar: calendarId };
            }
          }
        });

        setSavedRegionalMappings(mappings); // Always preserve server data

        // Check for stale calendar references
        const hasStale = Object.keys(mappings).length > Object.keys(validMappings).length;
        setHasStaleCalendars(hasStale);

        // Only set current mappings if in regional mode, and filter to valid calendars
        if (isRegional) {
          setRegionalMappings(validMappings);
        }
      }

      // Set selected calendar for single calendar mode (verify it still exists)
      if (config.calendar_auth_id) {
        const calendarExists = configData?.availableCalendars?.some(
          (cal: ICalendar) => cal.id === config.calendar_auth_id,
        );
        if (calendarExists) {
          setSelectedCalendar(config.calendar_auth_id.toString());
        }
      }
    }
  }, [configData]);

  const switchMode = (toRegional: boolean) => {
    setRegionalMode(toRegional);
    // When switching back to regional mode, restore saved mappings. Don't clear
    // mappings when switching to single mode - preserve them for round-trips.
    if (toRegional && Object.keys(savedRegionalMappings).length > 0) {
      setRegionalMappings(savedRegionalMappings);
    }
  };

  const handleSave = () => {
    const payload: any = {
      chatToken: token,
      enabled: Boolean(demoEnabled), // Ensure boolean type
      title: demoConfig.title?.trim() || 'Book a demo',
      description: demoConfig.description?.trim() || 'Schedule a personalized demo with our team',
      action_text: demoConfig.action_text?.trim() || 'Book Demo',
    };

    if (demoEnabled) {
      // Convert a {continent: {calendar: authIdStr}} UI map into the backend
      // {continent: {type, auth_id}} shape using the available calendars.
      const toBackendMappings = (uiMap: Record<string, { calendar: string }>) => {
        const mappings: Record<string, any> = {};
        Object.entries(uiMap).forEach(([continent, value]) => {
          if (value.calendar && value.calendar !== 'none') {
            const cal = configData?.availableCalendars?.find(
              (c: ICalendar) => c.id.toString() === value.calendar,
            );
            if (cal) {
              mappings[continent] = { type: cal.platform.toLowerCase(), auth_id: cal.id };
            }
          }
        });
        return mappings;
      };

      // calendar_config always carries availability (booking hours). Regional
      // mappings are preserved across mode switches so they survive round-trips.
      const calendarConfig: any = { availability };

      if (regionalMode) {
        calendarConfig.regional_mode = true;
        calendarConfig.mappings = toBackendMappings(regionalMappings);
      } else {
        calendarConfig.regional_mode = false;
        if (Object.keys(savedRegionalMappings).length > 0) {
          calendarConfig.mappings = toBackendMappings(savedRegionalMappings);
        }
        // Single calendar mode - use selectedCalendar for calendar_type/auth_id
        const selectedCalendarDetails = configData?.availableCalendars?.find(
          (cal: ICalendar) => cal.id.toString() === selectedCalendar,
        );
        if (selectedCalendarDetails) {
          payload.selectedCalendar = {
            type: selectedCalendarDetails.platform.toLowerCase(),
            authId: selectedCalendarDetails.id,
          };
        }
      }

      payload.calendar_config = calendarConfig;
    }

    saveConfig(payload, {
      onSuccess: () => {
        toast.success('Demo booking configuration saved successfully!');
      },
      onError: (err: Error) => {
        toast.error((err as any)?.response?.data?.error || 'Failed to save configuration');
      },
    });
  };

  // Re-hydrate all UI state from the last fetched server data — mirrors the load useEffect.
  const handleReset = useCallback(() => {
    if (!configData?.demoConfig) return;
    const config = configData.demoConfig;
    setDemoEnabled(Boolean(config.active_status));
    setDemoConfig({
      title: config.title || 'Book a demo',
      description: config.description || 'Schedule a personalized demo with our team',
      action_text: config.action_text || 'Book Demo',
    });
    const calendarConfig: any = config.calendar_config || {};
    const isRegional = Boolean(calendarConfig.regional_mode);
    setRegionalMode(isRegional);
    if (calendarConfig.availability && typeof calendarConfig.availability === 'object') {
      const saved = calendarConfig.availability;
      const defaults = buildDefaultAvailability();
      setAvailability({
        timezone: saved.timezone || defaults.timezone,
        slot_minutes: Number(saved.slot_minutes) || defaults.slot_minutes,
        buffer_minutes: Number(saved.buffer_minutes) || 0,
        days: WEEKDAYS.reduce((acc, d) => {
          const sd = saved.days?.[d.key];
          acc[d.key] = sd
            ? { enabled: Boolean(sd.enabled), start: sd.start || defaults.days[d.key].start, end: sd.end || defaults.days[d.key].end }
            : defaults.days[d.key];
          return acc;
        }, {} as Record<string, IDayAvailability>),
      });
    }
    if (calendarConfig.mappings) {
      const mappings: Record<string, { calendar: string }> = {};
      const validMappings: Record<string, { calendar: string }> = {};
      Object.entries(calendarConfig.mappings).forEach(([continent, mapping]: [string, any]) => {
        if (mapping.auth_id) {
          const calId = mapping.auth_id.toString();
          mappings[continent] = { calendar: calId };
          if (configData?.availableCalendars?.some((cal: ICalendar) => cal.id.toString() === calId)) {
            validMappings[continent] = { calendar: calId };
          }
        }
      });
      setSavedRegionalMappings(mappings);
      setHasStaleCalendars(Object.keys(mappings).length > Object.keys(validMappings).length);
      setRegionalMappings(isRegional ? validMappings : {});
    } else {
      setSavedRegionalMappings({});
      setRegionalMappings({});
      setHasStaleCalendars(false);
    }
    if (config.calendar_auth_id) {
      const exists = configData?.availableCalendars?.some((cal: ICalendar) => cal.id === config.calendar_auth_id);
      setSelectedCalendar(exists ? config.calendar_auth_id.toString() : '');
    } else {
      setSelectedCalendar('');
    }
  }, [configData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8 text-gray-600">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2">Loading demo booking configuration...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-red-600">Error loading demo booking configuration. Please try again.</p>
      </div>
    );
  }

  const calendars: ICalendar[] = configData?.availableCalendars || [];

  // At least one enabled weekday with a valid (end > start) window.
  const availabilityValid = WEEKDAYS.some((d) => {
    const cfg = availability.days[d.key];
    return cfg?.enabled && cfg.start && cfg.end && cfg.end > cfg.start;
  });

  const regionalAllEmpty = Object.values(regionalMappings)
    .every((mapping) => !mapping.calendar || mapping.calendar === 'none');

  // Blocking issues shown near the save action (mirror the disabled conditions).
  const issues: string[] = [];
  if (demoEnabled && calendars.length > 0) {
    if (!regionalMode && !selectedCalendar) issues.push('Select a connected calendar.');
    if (regionalMode && regionalAllEmpty) issues.push('Assign at least one calendar to a region.');
    if (!availabilityValid) issues.push('Enable at least one day with a valid time range.');
  }

  const saveDisabled = saving
    || isLoading
    || (demoEnabled && !selectedCalendar && !regionalMode)
    || (demoEnabled && regionalMode && regionalAllEmpty)
    || (demoEnabled && calendars.length > 0 && !availabilityValid);

  const timezoneChoices = TIMEZONE_OPTIONS.includes(availability.timezone)
    ? TIMEZONE_OPTIONS
    : [availability.timezone, ...TIMEZONE_OPTIONS];

  return (
    <div className="flex-1 min-h-0 flex flex-col">
    <div className="flex-1 min-h-0 overflow-y-auto p-6">
    <div className="space-y-6">
      {/* Enable demo booking */}
      <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-6 py-5 shadow-sm">
        <div className="min-w-0">
          <h3 className="text-[15px] font-semibold text-gray-900">Enable demo booking</h3>
          <p className="mt-1 max-w-xl text-sm text-gray-500">
            Adds a &ldquo;Book a demo&rdquo; flow to this chatbot, backed by your connected calendar.
          </p>
        </div>
        <Switch
          checked={demoEnabled}
          disabled={isLoading || saving}
          onCheckedChange={setDemoEnabled}
          aria-label="Enable demo booking"
        />
      </div>

      {demoEnabled && (
        <>
          {/* Calendar Configuration + Booking Availability side by side on wide screens */}
          <div className={cn('grid gap-6', calendars.length > 0 && 'lg:grid-cols-2')}>
            {/* Left column: Calendar Configuration. In 2-col mode the card is absolutely
                positioned to fill its cell, so the LEFT column never drives the row height —
                the row is sized by the RIGHT column and the card body scrolls to match. */}
            <div className={cn(calendars.length > 0 && 'lg:relative')}>
            <div className={cn(calendars.length > 0 && 'lg:absolute lg:inset-0')}>
            <SectionCard fill={calendars.length > 0} icon={CalendarDays} title="Calendar Configuration" subtitle="How calendars are assigned to visitors.">
            {calendars.length === 0 ? (
              <InfoBanner title="No calendars connected">
                Connect a calendar integration first in the Integrations section to enable demo booking.
              </InfoBanner>
            ) : (
              <>
                {hasStaleCalendars && (
                  <InfoBanner title="Some calendar connections may be outdated">
                    Some previously configured calendars are no longer available. Please review and update your assignments.
                  </InfoBanner>
                )}

                {/* Assignment mode (inline) — fixed, does not scroll */}
                <div className="lg:shrink-0">
                  <FieldLabel hint="Decide whether everyone shares one calendar or routes by region.">
                    Assignment mode
                  </FieldLabel>
                  <div role="radiogroup" aria-label="Assignment mode" className="grid gap-3 sm:grid-cols-2">
                    <RadioCard
                      checked={!regionalMode}
                      onSelect={() => switchMode(false)}
                      title="Single calendar"
                      subtitle="Same calendar for all visitors worldwide"
                    />
                    <RadioCard
                      checked={regionalMode}
                      onSelect={() => switchMode(true)}
                      title="Regional calendars"
                      subtitle="Different calendars based on visitor location"
                      trailing={Object.keys(savedRegionalMappings).length > 0 && !regionalMode ? (
                        <Badge variant="info" size="xs" className="bg-blue-100 text-blue-700">Configured</Badge>
                      ) : undefined}
                    />
                  </div>
                </div>

                {/* Single calendar selection */}
                {!regionalMode && (
                  <div className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
                    <FieldLabel hint="The calendar slots are pulled from and booked into.">
                      Connected calendar
                    </FieldLabel>
                    <div role="radiogroup" aria-label="Connected calendar" className="space-y-2 lg:min-h-0 lg:flex-1 lg:overflow-y-auto lg:pr-1">
                      {calendars.map((calendar) => (
                        <RadioCard
                          key={calendar.id}
                          checked={selectedCalendar === calendar.id.toString()}
                          onSelect={() => setSelectedCalendar(calendar.id.toString())}
                          title={calendar.display_name}
                          subtitle={calendar.platform}
                          trailing={(
                            <span className="font-mono text-[13px] text-gray-400">{calendar.email}</span>
                          )}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* Regional calendar mapping */}
                {regionalMode && (
                  <div className="lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
                    <FieldLabel hint="Visitors see the calendar mapped to their region; Default covers everyone else.">
                      Regional calendars
                    </FieldLabel>
                    <div className="overflow-hidden rounded-lg border border-gray-200 lg:min-h-0 lg:flex-1 lg:overflow-y-auto">
                      {CONTINENTS.map((continent, idx) => (
                        <div
                          key={continent.id}
                          className={cn(
                            'flex items-center justify-between gap-4 px-4 py-3',
                            idx !== CONTINENTS.length - 1 && 'border-b border-gray-100',
                          )}
                        >
                          <div className="min-w-0">
                            <div className="text-sm font-semibold text-gray-900">{continent.name}</div>
                            <div className="truncate text-[13px] text-gray-500">{continent.description}</div>
                          </div>
                          <div className="w-52 shrink-0">
                            <Select
                              value={regionalMappings[continent.id]?.calendar || 'none'}
                              onValueChange={(value) => {
                                setRegionalMappings((prev) => ({
                                  ...prev,
                                  [continent.id]: { calendar: value === 'none' ? '' : value },
                                }));
                              }}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select calendar" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No calendar assigned</SelectItem>
                                {calendars.map((calendar) => (
                                  <SelectItem key={calendar.id} value={calendar.id.toString()}>
                                    {calendar.display_name} ({calendar.platform})
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </SectionCard>
            </div>
            </div>

            {/* Right column: Booking card + Booking Availability */}
            <div className="space-y-6">
            {/* Visitor-facing card appearance */}
            <SectionCard
              icon={MessageSquareText}
              title="Booking card"
              subtitle="How the demo booking option appears to your visitors."
            >
              <div className="space-y-4">
                <div>
                  <FieldLabel>Title</FieldLabel>
                  <Input
                    value={demoConfig.title}
                    onChange={(e) => setDemoConfig({ ...demoConfig, title: e.target.value })}
                    placeholder="e.g., Book a demo"
                  />
                </div>
                <div>
                  <FieldLabel>Description</FieldLabel>
                  <Input
                    value={demoConfig.description}
                    onChange={(e) => setDemoConfig({ ...demoConfig, description: e.target.value })}
                    placeholder="e.g., Schedule a personalized demo with our team"
                  />
                </div>
                <div>
                  <FieldLabel>Button text</FieldLabel>
                  <Input
                    value={demoConfig.action_text}
                    onChange={(e) => setDemoConfig({ ...demoConfig, action_text: e.target.value })}
                    placeholder="e.g., Book Demo"
                  />
                </div>
              </div>
            </SectionCard>

          {/* Booking Availability */}
          {calendars.length > 0 && (
            <SectionCard
              icon={Clock}
              title="Booking Availability"
              subtitle="Slots generate in your business timezone and display in each visitor's local time."
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel>Business timezone</FieldLabel>
                  <Select
                    value={availability.timezone}
                    onValueChange={(value) => setAvailability((prev) => ({ ...prev, timezone: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {timezoneChoices.map((tz) => (
                        <SelectItem key={tz} value={tz}>{tz.replace(/_/g, ' ')}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <FieldLabel>Meeting duration</FieldLabel>
                  <Select
                    value={availability.slot_minutes.toString()}
                    onValueChange={(value) => setAvailability((prev) => ({ ...prev, slot_minutes: Number(value) }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {SLOT_DURATION_OPTIONS.map((min) => (
                        <SelectItem key={min} value={min.toString()}>{min} minutes</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">Available hours</p>
                <div className="rounded-lg border border-gray-200 px-4">
                  {WEEKDAYS.map((day, idx) => {
                    const cfg = availability.days[day.key];
                    const updateDay = (patch: Partial<IDayAvailability>) => setAvailability((prev) => ({
                      ...prev,
                      days: { ...prev.days, [day.key]: { ...prev.days[day.key], ...patch } },
                    }));
                    return (
                      <div
                        key={day.key}
                        className={cn(
                          'flex items-center gap-4 py-3',
                          idx !== WEEKDAYS.length - 1 && 'border-b border-gray-100',
                        )}
                      >
                        <div className="flex w-28 items-center gap-3">
                          <Switch
                            checked={cfg.enabled}
                            onCheckedChange={(checked) => updateDay({ enabled: checked })}
                            aria-label={day.label}
                          />
                          <span className={cn('text-sm font-semibold', cfg.enabled ? 'text-gray-900' : 'text-gray-400')}>
                            {day.label}
                          </span>
                        </div>
                        {cfg.enabled ? (
                          <div className="flex items-center gap-2">
                            <div className="relative">
                              <Clock className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                              <Input
                                type="time"
                                value={cfg.start}
                                onChange={(e) => updateDay({ start: e.target.value })}
                                className="w-[120px] pl-8"
                              />
                            </div>
                            <span className="text-sm text-gray-400">to</span>
                            <div className="relative">
                              <Clock className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                              <Input
                                type="time"
                                value={cfg.end}
                                onChange={(e) => updateDay({ end: e.target.value })}
                                className="w-[120px] pl-8"
                              />
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm italic text-gray-400">Closed</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </SectionCard>
          )}
            </div>
          </div>
        </>
      )}

    </div>
    </div>

    <StickyActionBar
      onReset={handleReset}
      onSave={handleSave}
      saving={saving}
      saveDisabled={saveDisabled}
      resetDisabled={saving || isLoading}
      saveLabel="Save Configuration"
      leftSlot={issues.length > 0 ? (
        <div className="flex items-center gap-2 text-sm text-amber-700">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{issues.join(' ')}</span>
        </div>
      ) : undefined}
    />
    </div>
  );
};

export default DemoBooking;
